import React, { useState } from 'react';
import { IconButton, Tooltip } from '@mui/material';
import { Translate } from 'react-localize-redux';

import { ButtonCancel, IconGenerator } from '../../../components/general/mini_components';
import { SnackbarTypeSeverity } from '../../../constants/types';
import { cancelRequestService } from '../../../services/request';
import ReasonModal from './ReasonModal';
import { canCancelRequest } from './requestModification';
import { IRequest } from './types';
import { useRequestMutation } from './useRequestMutation';

export interface CancelRequestActionCoreProps {
    request: IRequest;
    permissions: string[];
    loading: boolean;
    onConfirm: (reason: string) => void | Promise<boolean>;
    variant?: 'button' | 'icon';
}

// Presentational core: cancel control + mandatory-reason modal when policy allows.
export const CancelRequestActionCore: React.FC<CancelRequestActionCoreProps> = ({
    request,
    permissions,
    loading,
    onConfirm,
    variant = 'button',
}) => {
    const [open, setOpen] = useState(false);

    if (!canCancelRequest(request, permissions)) {
        return null;
    }

    const openModal = (e?: React.MouseEvent) => {
        e?.stopPropagation();
        setOpen(true);
    };

    return (
        <>
            <ReasonModal
                open={open}
                closeModal={() => setOpen(false)}
                onConfirm={onConfirm}
                loading={loading}
                titleId="pages.hospital.services.request.cancel.title"
                bodyId="pages.hospital.services.request.cancel.modal"
                reasonLabelId="pages.hospital.services.request.cancel.reason_label"
                reasonTestId="cancel-reason"
            />
            {variant === 'icon' ? (
                <Tooltip title={<Translate id="pages.hospital.services.request.cancel.button" />} arrow>
                    <IconButton
                        size="medium"
                        data-testid="cancel-request-icon"
                        aria-label="cancel-request"
                        onClick={openModal}
                    >
                        <IconGenerator type="delete" size="medium" />
                    </IconButton>
                </Tooltip>
            ) : (
                <ButtonCancel onClick={openModal} data-testid="cancel-request">
                    <Translate id="pages.hospital.services.request.cancel.button" />
                </ButtonCancel>
            )}
        </>
    );
};

interface CancelRequestActionProps {
    request: IRequest;
    uuidInvestigation: string;
    permissions: string[];
    onCancelled?: (updated?: IRequest) => void;
    setShowSnackbar: (snackbar: { show: boolean; message?: string; severity?: SnackbarTypeSeverity }) => void;
    variant?: 'button' | 'icon';
}

const CancelRequestAction: React.FC<CancelRequestActionProps> = ({
    request,
    uuidInvestigation,
    permissions,
    onCancelled = () => {},
    setShowSnackbar,
    variant = 'button',
}) => {
    const { loading, run } = useRequestMutation(setShowSnackbar, onCancelled);

    const handleConfirm = (reason: string) =>
        run(() => cancelRequestService(uuidInvestigation, request.id, reason), 'pages.hospital.services.request.cancel.success');

    return (
        <CancelRequestActionCore
            request={request}
            permissions={permissions}
            loading={loading}
            onConfirm={handleConfirm}
            variant={variant}
        />
    );
};

export default CancelRequestAction;
